import { CloudImagePipe } from './../../../../infrastructure/shared/pipes/cloud-image.pipe';
import { CLOUD_IMAGE_URL } from './../../../../helpers/images';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart, Router
} from '@angular/router';
import { AuthService } from '@app/core/auth/auth.service';
import { UserService } from '@app/core/auth/user.service';
import { ListingCategoryService } from '@app/core/services/listing-category.service';
import { ListingService } from '@app/core/services/listing.service';
import { MapsService } from '@app/core/services/maps.service';
import { MessageService } from '@app/core/services/message.service';
import { ChatListingItem } from '@app/models/chat-listing-item.model';
import { User } from '@app/models/user.model';
import { LoginComponent } from '@app/shared/components/login/login.component';
import { DateUtil, DEFAULT_DATE_FORMAT } from '@app/shared/utils/date-util.service';
import { ImageUtil } from '@app/shared/utils/image.util.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotifierService } from 'angular-notifier';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil, debounceTime, withLatestFrom } from 'rxjs/operators';
import { CHAT_LISTING_TYPE } from 'src/app/helpers/constants';
import { Listing } from 'src/app/models/listing.model';

@Component({
  selector: 'app-listing-details',
  templateUrl: './listing-details.component.html',
  styleUrls: ['./listing-details.component.scss']
})
export class ListingDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  unsubscribe$ = new Subject();
  // Single date picker
  propsSDP: any;
  formSRP: any;
  selectedDate: Date;
  selectedDateAdv: Date;
  dateRangePickerProps: any;
  formDateRangePickerProps: any;
  selectedDateRange: any;
  listingDetails: Listing;
  location: string[];
  dates = {
    minDate: null,
    maxDate: null,
    fromDate: null,
    toDate: null,
    initToDate: null,
    initFromDate: null
  };
  reservedDates: any[] = [];
  loading = false;
  googleMapsPlacesResult: google.maps.places.PlaceResult;
  hasNoAvailability = false;
  googleMapInfoWindowOpen = true;

  /**
   * listing user details
   */
  listingUser$: Observable<User>;

  /**
   * preselected rent date range
   */
  preSelectedRentDate: any;

  currentUser: User;

  config = {
    direction: 'horizontal',
    keyboard: true,
    mousewheel: false,
    scrollbar: true,
    pagination: true
  };

  parentCategory = '';
  childCategory = '';

  listingImagesPosted = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private listingService: ListingService,
    private userService: UserService,
    private messageService: MessageService,
    private dateUtils: DateUtil,
    private imageUtil: ImageUtil,
    private mapsService: MapsService,
    private authService: AuthService,
    private ngbModal: NgbModal,
    private categoryService: ListingCategoryService,
    private notifier: NotifierService,
    private cloudImagePipe: CloudImagePipe
  ) {
    // Single date picker conf
    this.propsSDP = {
      startDatePlaceholderText: 'Available From',
      endDatePlaceholderText: 'Available To',
      required: true,
      keepOpenOnDateSelect: true,
      focusedInput: 'startDate'
    };
    this.dateRangePickerProps = Object.assign({}, this.propsSDP);

    this.formSRP = {
      startDatePlaceholderText: 'Start Date',
      endDatePlaceholderText: 'Return Date',
      required: true
    };
    this.formDateRangePickerProps = Object.assign({}, this.formSRP);

    this.router.events.subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading = true;
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          break;
        }
        default: {
          break;
        }
      }
    });

    this.route.data
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.listingInitialization(data);
      });

    this.authService.user
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(user => this.currentUser = user);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(
        take(1)
      ).subscribe(queryParams => {
        const initToAndFromDate = this.listingService.extractToAndFromDateFromRouteQueryParams(queryParams);

        const { initToDate, initFromDate } = initToAndFromDate;
        if (this.dates.minDate <= initFromDate
          && this.dates.maxDate >= initToDate) {
          this.dates = { ...this.dates, ...initToAndFromDate };
        }
      });

    this.listingUser$ = this.userService.getUserDetails(this.listingDetails.user);
  }

  ngAfterViewInit() {
    this.isOwnListingAsync()
      .pipe(
        take(1),
        debounceTime(300)
      )
      .subscribe(isOwnListing => {
        if (isOwnListing) {
          this.notifier.notify(
            'default',
            'You are currently viewing your own listing.'
          );
        }
      });
  }

  get listingDescription() {
    return this.listingDetails
      ? this.listingDetails.description.replace(
        new RegExp('\n', 'g'),
        '<br>'
      )
      : 'No listing description';
  }

  async listingInitialization(data) {
    this.listingDetails = data.listingDetails;

    this.listingImagesPosted = this.generateListingImages();
    if (this.listingDetails.address && this.listingDetails.address.placeId) {
      this.mapsService.getPlaceResultFromPlaceId(this.listingDetails.address.placeId)
        .pipe(take(1))
        .subscribe(placeResult => {
          this.applyLocation(placeResult.formatted_address);
          this.googleMapsPlacesResult = placeResult;
        });
    } else if (this.listingDetails.location) {
      this.applyLocation(this.listingDetails.location);
    }

    if (data.listingDetails.category && data.listingDetails.category.parentId) {
      const parentCategory: any = await this.categoryService.getItem(data.listingDetails.category.parentId);
      this.parentCategory = parentCategory.name;
      if (data.category) {
        this.childCategory = data.category.name;
      }
    }

    const startDate = new Date(this.listingDetails.startDate ? this.listingDetails.startDate.toDate() : new Date());
    // this.dates.minDate = new Date() < startDate ? startDate : new Date();
    const currentDate = new Date();
    this.dates.minDate = startDate;
    this.dates.fromDate = this.dates.minDate;
    this.dates.maxDate = this.listingDetails.endDate ? new Date(this.listingDetails.endDate.toDate()) : null;
    if (currentDate >= this.dates.maxDate && this.dates.maxDate != null) {
      this.hasNoAvailability = true;
    }
    if (this.dates.maxDate == null) {
      this.dates.maxDate = null;
      this.dates.minDate = currentDate;
    }
    this.listingService.getRentsWithLimit(data.listingDetails.id)
      .pipe(
        take(1)
      ).subscribe(rents => {
        this.reservedDates = rents;
        let datesToCompare = [];
        if (this.reservedDates && this.reservedDates.length >= 1) {
          datesToCompare = this.reservedDates.filter((d) => {
            return new Date(d.endDate.seconds * 1000) >= currentDate;
          });
          let lastBookingDate = null;
          let startDateSource = null;
          if (datesToCompare.length >= 1) {
            // tslint:disable-next-line: prefer-for-of
            for (const x of datesToCompare) {
              x['startDateTime'] = new Date(x.startDate.seconds * 1000);
              x['endDateTime'] = new Date(x.endDate.seconds * 1000);
            }
            for (let x = datesToCompare.length - 1; x >= 0; x--) {
              lastBookingDate = new Date(datesToCompare[x].endDate.seconds * 1000).addDays(1);
              const yDate = (x === 0) ? this.dates.maxDate : new Date(datesToCompare[x - 1].startDate.seconds * 1000);
              if (yDate != null) {
                const diff = this.dateUtils.getDifference(yDate, lastBookingDate);
                if (diff.day >= 2) {
                  startDateSource = lastBookingDate;
                  break;
                }
              }
            }
            if (startDateSource == null && lastBookingDate !== null) {
              startDateSource = (lastBookingDate as Date).addDays(1);
            }
          } else {
            if (this.dates.fromDate < currentDate) {
              startDateSource = currentDate;
            }
          }

          this.dates = null;
          this.dates = {} as any;
          this.dates.maxDate = this.listingDetails.endDate ? new Date(this.listingDetails.endDate.toDate()) : null;
          if (this.dates.maxDate == null) {
            this.dates.minDate = currentDate;
          } else {
            this.dates.minDate = startDate;
          }

          this.dates.fromDate = startDateSource || currentDate;
          this.dates.toDate = this.dates.fromDate;
          this.dates.initFromDate = this.dates.fromDate;
          this.dates.initToDate = this.dates.toDate;
        }
      });
  }

  changeDateRangeCal1(event) {
    const from = this.dateUtils.formatNgbDate(event.from, DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT);
    const to = this.dateUtils.formatNgbDate(event.to, DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT);
    this.preSelectedRentDate = {
      from,
      to
    };
  }

  private applyLocation(locationString: string) {
    if (locationString) {
      this.location = locationString.split(',');
      if (this.location.length > 1) {
        this.location[0] = this.location[0] + ',' + this.location[1];
      }
    }
  }

  resetValues(event) { }

  sendMessage(listingDetails: Listing) {
    this.authService.user
      .pipe(take(1))
      .subscribe(user => {
        if (user) {
          const initiatorUserDetails: User = user;

          this.userService.getUserDetails(listingDetails.user)
            .pipe(take(1))
            .subscribe(receiverData => {
              const chatListingItem: ChatListingItem = {
                type: CHAT_LISTING_TYPE.LISTING,
                listingId: this.listingDetails.id || this.listingDetails.objectID
              };

              this.messageService.initializeChatRoom(initiatorUserDetails, receiverData, chatListingItem)
                .pipe(take(1))
                .subscribe(data => {
                  this.router.navigate(['messaging', data.id]);
                });
            });
        } else {
          this.ngbModal.open(LoginComponent);
        }
      })
  }

  /**
   * TODO jumangitjs handle pre rent
   * check if listing owner has stripe external account, accepted TOS
   * email listing owner if an attempt was made but not pushed through due to no payout
   */
  rentItem(itemID) {
    this.authService.user
      .pipe(
        take(1),
        withLatestFrom(this.listingUser$)
      )
      .subscribe(([user, listingUser]) => {
        let queryParams;
        if (user) {
          if (!!listingUser.stripeTosAccepted) {
            if (this.preSelectedRentDate) {
              queryParams = this.preSelectedRentDate;
            }
            this.router.navigate(['booking', itemID], { queryParams });
          } else {
            this.notifier.notify(
              'warning',
              'Sadly this listing is unable to take bookings. We will notify the user to enable listing checkout.'
            );

            // TODO notify listing user to accept tos and or payout
          }
        } else {
          this.ngbModal.open(LoginComponent);
        }
      });
  }

  getBackgroundImageStyle(url: string) {
    return this.imageUtil.getBackgroundImageStyle(url);
  }

  toggleInfoWindow(infowindow: any) {
    if (infowindow.isOpen) {
      infowindow.close();
    } else {
      infowindow.open();
      infowindow.isOpen = true;
    }
  }

  isOwnListingAsync(): Observable<boolean> {
    return this.authService.user
      .pipe(
        map(
          user => this.listingDetails
            && this.listingDetails.user
            && this.listingDetails.user === user.uid
        )
      );
  }

  isOwnListing(): boolean {
    return this.listingDetails
      && this.listingDetails.user
      && this.currentUser
      && this.currentUser.uid === this.listingDetails.user;
  }

  generateListingImages(): any[] {
    if (this.listingDetails != null && this.listingDetails.images != null) {
      const images = [];
      for (const x of this.listingDetails.images) {
        let finalUrl = this.cloudImagePipe.transform(x);

        images.push({
          image: finalUrl,
          thumbImage: finalUrl,
        });
      }

      return images;
    }
    return [];
  }
}
